import { useEffect, useState } from 'react';
import { Alert, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import AuthorCard from '../components/AuthorCard';
import GradientBackground from '../components/GradientBackground';
import colors from '../theme';
import { RootStackParamList } from '../navigation/RootNavigator';
import { AudiobookPerson, getPeople } from '../api/audiobooks';
import { savePreferences } from '../api/library';

type Props = NativeStackScreenProps<RootStackParamList, 'PreferencesAuthors'>;

export default function PreferencesAuthorsScreen({ navigation, route }: Props) {
  const [people, setPeople] = useState<AudiobookPerson[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getPeople({ per_page: 30 }).then(setPeople).catch(() => setPeople([]));
  }, []);

  const toggleAuthor = (personId: number) => {
    setSelected((prev) =>
      prev.includes(personId) ? prev.filter((item) => item !== personId) : [...prev, personId]
    );
  };

  const handleContinue = async () => {
    setSaving(true);
    try {
      await savePreferences(route.params?.topicIds ?? [], selected);
      navigation.navigate('Home');
    } catch (error) {
      Alert.alert('Không thể lưu sở thích', error instanceof Error ? error.message : 'Vui lòng thử lại.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={styles.screen}>
      <GradientBackground fullHeight />
      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <View style={styles.topRow}>
          <Pressable onPress={() => navigation.navigate('Home')} hitSlop={10}>
            <Text style={styles.skip}>Bỏ qua</Text>
          </Pressable>
        </View>
        <Text style={styles.title}>Bạn thích người đọc sách nào?</Text>
        <Text style={styles.subtitle}>Chọn người bạn muốn nghe nhiều hơn trong các gợi ý.</Text>
        <View style={styles.progressBarOuter}>
          <View style={styles.progressBarInner} />
        </View>
        <View style={styles.authorGrid}>
          {people.map((person) => (
            <AuthorCard
              key={person.id}
              name={person.name}
              selected={selected.includes(person.id)}
              onPress={() => toggleAuthor(person.id)}
            />
          ))}
        </View>
        <View style={styles.actions}>
          <Pressable style={styles.altButton} onPress={() => navigation.goBack()}>
            <Text style={styles.altButtonText}>Quay lại</Text>
          </Pressable>
          <Pressable style={styles.mainButton} onPress={handleContinue} disabled={saving}>
            <Text style={styles.mainButtonText}>{saving ? 'Đang lưu...' : 'Tiếp tục'}</Text>
          </Pressable>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'rgb(242, 249, 253)',
  },
  container: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingTop: 44,
    paddingBottom: 34,
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 20,
  },
  skip: {
    color: colors.buttonText,
    fontSize: 14,
    fontWeight: '900',
  },
  title: {
    color: colors.buttonText,
    fontSize: 22,
    fontWeight: '900',
    marginBottom: 10,
  },
  subtitle: {
    color: colors.button,
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 20,
    fontWeight: '800',
  },
  progressBarOuter: {
    height: 7,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.6)',
    overflow: 'hidden',
    marginBottom: 22,
  },
  progressBarInner: {
    width: '100%',
    height: '100%',
    backgroundColor: colors.button,
  },
  authorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    rowGap: 12,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 28,
    gap: 12,
  },
  altButton: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderRadius: 18,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  altButtonText: {
    color: colors.textSecondary,
    fontWeight: '900',
    fontSize: 14,
  },
  mainButton: {
    flex: 1,
    backgroundColor: colors.button,
    borderRadius: 18,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mainButtonText: {
    color: colors.buttonText,
    fontWeight: '900',
    fontSize: 14,
  },
});
